import { useEffect, useRef, type RefObject } from 'react';

let bodyScrollLockCount = 0;
let bodyOverflowBeforeFirstLock = '';

/**
 * Adquire uma trava compartilhada do scroll do body.
 *
 * Diálogos podem se sobrepor (por exemplo, editor + confirmação). Cada trava
 * precisa ser liberada antes de restaurar o overflow original; restaurar o valor
 * capturado por cada diálogo deixa `overflow: hidden` preso quando fecham fora
 * da ordem em que abriram.
 */
export function acquireBodyScrollLock(): () => void {
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeFirstLock = document.body.style.overflow;
  }
  bodyScrollLockCount += 1;
  document.body.style.overflow = 'hidden';

  let released = false;
  return () => {
    if (released) return;
    released = true;
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) {
      document.body.style.overflow = bodyOverflowBeforeFirstLock;
    }
  };
}

/** Trava o scroll do body enquanto `locked`, com suporte a diálogos concorrentes. */
export function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    return acquireBodyScrollLock();
  }, [locked]);
}

/** Fecha em Escape, dá foco inicial ao painel e devolve o foco ao abridor ao fechar. */
export function useDialogA11y(
  open: boolean,
  onClose: () => void,
  panelRef: RefObject<HTMLElement | null>,
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
      opener?.focus?.();
    };
  }, [open, panelRef]);
}
